export interface GetCaseByIdRequest {
  caseId: number;
}
export enum CloseCaseLoop {
  PHONE = 'Phone',
  CHAT = 'Chat',
}

// === Case History ===

export interface CaseHistoryEntry {
  caseId: number;
  workspaceId: number;
  action: 'created' | 'closed';
  timestamp: string; // ISO 8601
  replacedByCaseId?: number;
  status?: string;
}

// === Dropdown Types ===

export interface CaseTypeItem {
  id: number;
  name: string;
  category: string;
  priority: string;
}

export interface EmployeeItem {
  id: number;
  employeeIdentifier: string;
  firstName: string;
  lastName: string;
  phone: string;
  mobile: string;
  email: string;
  workEmail: string;
}

export interface InternalUserItem {
  id: number;
  userName: string;
  name: string;
  email: string;
}

// === Request / Response Types ===

export interface CreateCaseRequest {
  contactId: string;
  workspaceId: number;
  status: string;
  categoryCode: string;
  callType: 'call' | 'chat';
  caseTypeId: number;
  categoryPriority?: string;
  callerName: string;
  subject: string;
  description: string;

  subCaseType: string;
  resolution: string;
  phoneNumber: string;
  email: string;
  closeCaseLoop: CloseCaseLoop;
  origination?: string;
  employeeId?: number;
  reportedByEmployeeId?: number;
  assignedTo?: number;
  resolutionType?: string;
  reportedByName?: string;
  reportingMethod?: string;
  clientContactId?: string;
  internalNotes?: string;
  /** Internal ClientSpace case ID to link as the related case. */
  relatedCaseId?: number;
}

/** Predefined resolution type values returned by the backend. */
export type ResolutionType =
  | 'AI Resolved'
  | 'Agent Assisted'
  | 'Customer Abandoned';

/** Fields that Boost AI can detect and pre-fill into the case creation form. */
export interface CasePrefillFields {
  subject?: string;
  description?: string;
  categoryCode?: string;
  caseTypeId?: number;
  subCaseType?: string;
  priority?: string;
  resolution?: string;
  resolutionType?: string;
  reportedByName?: string;
  origination?: string;
  callerName?: string;
  phoneNumber?: string;
  email?: string;
}

export type CreateCaseResponse = {
  caseId: number;
  caseNumber: string;
  /** Set when a related case was requested: whether linking it succeeded. */
  relatedCaseLinked?: boolean;
};

// === Time Tracking ===

export interface TimeTrackingCategory {
  id: number;
  name: string;
}

export interface GetTimeTrackingCategoriesResponse {
  categories: TimeTrackingCategory[];
}

/** ClientSpace time tracking record logged on a case. */
export interface CaseTimer {
  id: number;
  startDate: string; // ISO 8601
  endDate: string; // ISO 8601
  minutes: number;
  userId: number;
  userName: string;
  /** Null when ClientSpace returns a category ESP does not know. */
  categoryId: number | null;
  categoryName: string;
  notes: string;
}

export interface GetCaseTimersResponse {
  timers: CaseTimer[];
}

/** Only the fields that are sent are changed. */
export interface UpdateCaseTimerRequest {
  caseId: number;
  timerId: number;
  timeTrackingCategoryId?: number;
  /** ISO 8601 */
  startDate?: string;
  minutes?: number;
  notes?: string;
}

export interface LinkRelatedCaseRequest {
  /** ESP case the interaction belongs to. */
  caseId: number;
  /** Internal ClientSpace ID of the existing case to link as related. */
  relatedCaseId: number;
  contactId: string;
}

export interface LinkRelatedCaseResponse {
  relatedCaseNumber: string;
  /** True when the related case was open and received the note and Related Case value. */
  relatedCaseUpdated: boolean;
}

export interface CaseStatusOption {
  code: string;
  name: string;
}

export interface GetCaseStatusesResponse {
  statuses: CaseStatusOption[];
}

export interface CaseCategory {
  name: string;
  code: string;
}

export interface GetCaseCategoriesResponse {
  categories: CaseCategory[];
}

export interface CaseTypeOption {
  id: number;
  name: string;
}

export interface GetCaseTypesResponse {
  caseTypes: CaseTypeItem[];
}

export interface GetCaseTypesByCategoryResponse {
  caseTypes: CaseTypeItem[];
}

export interface ClientContactItem {
  fkContactIDClientContact: string;
  displayName: string;
}

export interface GetClientContactsResponse {
  contacts: ClientContactItem[];
}

export interface GetEmployeesByWorkspaceResponse {
  employees: EmployeeItem[];
}

export interface GetInternalUsersResponse {
  users: InternalUserItem[];
}

export interface ResolutionTypeOption {
  id: string;
  name: string;
}

export interface GetResolutionTypesResponse {
  resolutionTypes: ResolutionTypeOption[];
}

export interface GetCasePrioritiesResponse {
  priorities: {
    id: string;
    name: string;
  }[];
}

export interface GetSubCaseTypeTreeResponse {
  subCaseTypeTree: Record<string, string[]>;
}

export interface GetCaseOriginationsResponse {
  originations: {
    id: string;
    name: string;
  }[];
}
export interface GetCaseResolutionResponse {
  resolution: string;
}

export interface CaseInformationResponse {
  /** The case ID */
  id: number;
  /** The case subject */
  subject: string;
  /** The case status */
  status: string;
  /** The case notes */
  notes: string;
  /** The case description */
  description: string;
  /** The case assigned to user ID */
  assignedTo: number;
  /** The case assigned to user name */
  assignedToName: string;
  /** The case category */
  category?: string;
  /** The case priority */
  priority?: string;
  /** The case type */
  caseType?: CaseTypeOption;
  /** The sub-case type */
  subCaseType?: string;
  /** The case origination */
  origination?: string;
  /** The case close loop status */
  closeCaseLoop?: CloseCaseLoop;
  /** The case resolution (single line) */
  resolution?: string;
  /** The case phone number */
  phoneNumber?: string;
  /** The case email */
  email?: string;
  /** The case reporting method */
  reportingMethod?: string;
  /** The employee ID linked to this case (0 if not set) */
  employeeId?: number;
  /** The reported by employee ID (0 if not set) */
  reportedByEmployeeId?: number;
  /** The resolution type */
  resolutionType?: string;
  reportedByName?: string;
  /** Client contact ID for Client Request origination (empty if not set; the API returns a number) */
  clientContactId?: string | number;
}

export interface UpdateCaseRequest {
  caseId: number;
  callerName?: string;
  subject?: string;
  description?: string;
  assignedTo?: number;
  notes?: string;
  status?: string;
  contactId?: string;
  categoryCode?: string;
  priority?: string;
  caseTypeId?: number;
  subCaseType?: string;
  origination?: string;
  closeCaseLoop?: CloseCaseLoop;
  resolution?: string;
  resolutionType?: string;
  reportedByName?: string;
  phoneNumber?: string;
  email?: string;
  employeeId?: number;
  reportedByEmployeeId?: number;
  reportingMethod?: string;
  clientContactId?: string;
}

export type UpdateCaseResponse = void;

export interface GetAiSummaryRequest {
  contactId: string;
}

export interface GetCaseResolutionRequest {
  contactId: string;
}
export interface GetAiSummaryResponse {
  summary: string;
}
